import React, { useRef } from 'react';

import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Business } from '@material-ui/icons';

import LoadingButton from '../common/LoadingButton';
import TextField from '../common/NewTextField';

const useStyles = makeStyles(() => ({
  buttonsContainer: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
    marginTop: 24,
    width: '100%',
  },
  cancelButton: {
    borderRadius: '4px',
    color: '#42526E',
    flex: '1',
    fontWeight: '500',
    textTransform: 'none',
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    overflow: 'hidden',
  },
  facilityInfo: {
    alignItems: 'center',
    color: '#6B778C',
    display: 'flex',
    fontSize: 14,
    fontWeight: 'bold',
    gap: 8,
    marginBottom: 16,
    marginTop: 8,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 12,
    marginRight: '-16px',
    marginTop: 16,
    overflowY: 'auto',
    paddingRight: 16,
    width: 'min(100%, 400px)',
  },
  label: {
    fontWeight: 'bold',
  },
  submitButton: {
    '&:hover': {
      backgroundColor: '#2D95C4',
    },
    backgroundColor: '#36B2EC',
    borderRadius: 4,
    color: 'white',
    flex: '1',
    fontWeight: 500,
    textTransform: 'none',
  },
  subtitle: {
    color: '#6B778C',
    fontSize: 14,
    textAlign: 'center',
  },
  title: {
    color: '#172B4D',
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 8,
    textAlign: 'center',
  },
}));

const AddDepartmentForm = ({
  onAddDepartment,
  defaultData = {},
  isSubmitting,
  onCancel,
  title = 'Add New Department',
  subtitle = 'Add a new department to facility',
  facilityName,
  isEditing = false,
}) => {
  const classes = useStyles();

  // Field refs
  const nameRef = useRef();
  const codeRef = useRef();

  const onAddClick = () => {
    const name = nameRef.current?.getValue();
    const code = codeRef.current?.getValue();

    nameRef.current?.clearError();
    codeRef.current?.clearError();

    let hasErrors = false;

    if (!name?.trim()) {
      nameRef.current?.setError('Department name is required');
      hasErrors = true;
    }

    if (!hasErrors) {
      const validData = {
        code: code?.trim() || null,
        facilityId: defaultData?.facilityId,
        name: name.trim(),
      };
      onAddDepartment(validData);
    }
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography variant="h4" className={classes.title}>
            {title}
          </Typography>
          <Typography variant="body1" className={classes.subtitle}>
            {subtitle}
          </Typography>
        </div>

        {facilityName && (
          <div className={classes.facilityInfo}>
            <Business fontSize="small" />
            <span>{facilityName}</span>
          </div>
        )}

        <div className={classes.formContainer}>
          <Box>
            <Typography className={classes.label}>Department Name</Typography>
            <TextField ref={nameRef} name="name" placeholder="Enter department name" required value={defaultData?.name || ''} />
          </Box>

          <Box>
            <Typography className={classes.label}>Department Code</Typography>
            <TextField ref={codeRef} name="code" placeholder="Enter department code" value={defaultData?.code || ''} />
          </Box>
        </div>

        <div className={classes.buttonsContainer}>
          <Button variant="outlined" onClick={onCancel} className={classes.cancelButton}>
            Cancel
          </Button>
          <LoadingButton variant="contained" onClick={onAddClick} loading={isSubmitting} className={classes.submitButton}>
            {isEditing ? 'Update Department' : 'Add Department'}
          </LoadingButton>
        </div>
      </div>
    </>
  );
};

export default AddDepartmentForm;
